import React from "react";
import {
  Form,
  Input,
  DatePicker,
  Upload,
  message,
  Button,
  Row,
  Col,
  Typography,
} from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { getUploadUrlApi } from "../../../services/common.services";
import { convertToFormData } from "../../../helpers/utility";
import {
  CameraOutlined,
  CloseCircleFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { getFileIcon } from "../../../helpers/getFileIcon";
import "./InvestigationsIp.css";
import "./InvestigationModal.css";
import useQuery from "../../../hooks/useQuery";
import useReceptionistMobilePickerProps from "../../Receptionist/receptionistMobilePickerProps";

const { Dragger } = Upload;

const MAX_FILE_SIZE_MB = 30;

export const alignInvestigationDatePickerBelow = (open, triggerEl) => {
  if (!open) return;

  const position = () => {
    const dropdown = document.querySelector(
      ".investigation-date-picker-dropdown.ant-picker-dropdown"
    );
    const trigger =
      triggerEl || document.querySelector(".investigation-date-picker");

    if (!dropdown || !trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panel = dropdown.querySelector(".ant-picker-panel-container");
    const panelHeight = panel?.offsetHeight || 320;
    const gap = 8;
    const viewportPad = 12;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    const placeAbove =
      spaceBelow < panelHeight && spaceAbove > spaceBelow;

    const top = placeAbove
      ? Math.max(viewportPad, rect.top - panelHeight - gap)
      : Math.min(
          rect.bottom + gap,
          Math.max(viewportPad, window.innerHeight - panelHeight - viewportPad)
        );

    const maxWidth = Math.min(
      Math.max(rect.width, 280),
      window.innerWidth - viewportPad * 2
    );
    const left = Math.min(
      Math.max(viewportPad, rect.left),
      window.innerWidth - maxWidth - viewportPad
    );

    dropdown.style.position = "fixed";
    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
    dropdown.style.right = "auto";
    dropdown.style.bottom = "auto";
    dropdown.style.transform = "none";
    dropdown.style.width = `${maxWidth}px`;
    dropdown.style.maxWidth = `calc(100vw - ${viewportPad * 2}px)`;
    dropdown.style.zIndex = "1600";
  };

  const trigger =
    triggerEl || document.querySelector(".investigation-date-picker");
  trigger?.scrollIntoView({ block: "center", behavior: "smooth" });

  window.setTimeout(position, 0);
  window.setTimeout(position, 50);
  window.setTimeout(position, 120);

  window.setTimeout(() => {
    const dropdown = document.querySelector(
      ".investigation-date-picker-dropdown.ant-picker-dropdown"
    );
    const panel = dropdown?.querySelector(".ant-picker-panel-container");
    if (!panel || typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver(() => {
      position();
    });
    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    window.setTimeout(() => observer.disconnect(), 15000);
  }, 0);
};

const InitialInvestigationForm = ({
  editData,
  setUploadFile,
  setUploadFileLoading,
  uploadFile,
}) => {
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const datePickerPopupClass = [
    "investigation-date-picker-dropdown",
    mobilePickerProps.popupClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const isCompactPicker = Object.keys(mobilePickerProps).length > 0;
  const params = useQuery();
  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  const handleRemoveFile = (fileToRemove) => {
    const filteredFiles = uploadFile.filter(
      (file) => file.path !== fileToRemove.path
    );
    setUploadFile(filteredFiles);
    message.success("Removed file successfully");
  };

  const uploadProps = {
    beforeUpload: (file) => {
      let file_ext = file.name.split(".").slice(-1)[0];
      const fileType =
        file_ext === "png" ||
        file_ext === "jpg" ||
        file_ext === "jpeg" ||
        file_ext === "svg" ||
        file.type === "application/pdf";

      if (!fileType) {
        message.error(`${file.name} is not a valid file`);
        return Upload.LIST_IGNORE;
      }
      const isValidSize = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
      if (!isValidSize) {
        message.error(`File must be ${MAX_FILE_SIZE_MB}MB or smaller`);
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    async onChange(info) {
      if (info.file.status === "done") {
        setUploadFileLoading(true);
        try {
          const res = await getUploadUrlApi(
            `path_key=${info.file.name}&admission_type=IP&admission_id=${params?.patient}`
          );
          const formData = convertToFormData(res, info);
          await axios({
            method: "POST",
            url: res?.data?.url,
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          setUploadFile((prevState) => [
            ...prevState,
            { path: res?.data?.fields?.key, type: info.file.type },
          ]);
        } catch (err) {
          message.error(err?.response?.data?.message);
        } finally {
          setUploadFileLoading(false);
        }
      }
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const datePicker = (
    <Form.Item label="Date" name="date">
      <DatePicker
        className={`${inputStyles.mdDateInput} investigation-date-picker ia-investigation-control`}
        disabledDate={disabledDate}
        allowClear
        clearIcon={
          <CloseCircleFilled
            className="investigation-date-clear"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        }
        style={{ width: "100%", maxWidth: "100%" }}
        {...mobilePickerProps}
        popupClassName={datePickerPopupClass}
        placement="bottomLeft"
        getPopupContainer={() => document.body}
        onOpenChange={(open) => {
          if (isCompactPicker) {
            alignInvestigationDatePickerBelow(
              open,
              document.querySelector(".investigation-date-picker")
            );
          }
        }}
      />
    </Form.Item>
  );

  return (
    <>
      <Form.Item label="Investigation Name" name="name">
        <Input
          className={`${inputStyles.mdInput} ia-investigation-control`}
          style={{ width: "100%", maxWidth: "100%" }}
        />
      </Form.Item>

      {datePicker}

      <Form.Item label="Upload Initial Investigations" name="upload">
        <Dragger
          className="ia-investigation-upload"
          style={{ width: "100%", maxWidth: "100%" }}
          accept=".png,.jpg,.jpeg,.svg,application/pdf"
          {...uploadProps}
          multiple
          customRequest={dummyRequest}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <CameraOutlined />
          </p>
          <p className="ant-upload-text">
            <span style={{ color: "#406AFF" }}>Click to Capture </span> or
            upload
          </p>
        </Dragger>

        <Row gutter={[12, 12]} style={{ padding: "8px 0 0" }}>
          {uploadFile.map((item, index) => (
            <Col
              className="inv-upload-file"
              xs={8}
              sm={6}
              md={6}
              key={item.path || index}
            >
              <span style={{ display: "flex" }}>
                {getFileIcon(item)}
                <Button
                  className="inv-upload-del-btn"
                  type="link"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveFile(item)}
                />
              </span>

              <Typography.Text ellipsis>
                {item.path.replace("curengo/", "")}
              </Typography.Text>
            </Col>
          ))}
        </Row>
      </Form.Item>
    </>
  );
};

export default InitialInvestigationForm;
